export interface ParsedSentryDsn {
  dsn: string;
  publicKey: string;
  host: string;
  projectId: string;
  storeUrl: string;
}

/** Returns null when no DSN is configured or the value is not a Sentry DSN. */
export function parseSentryDsn(
  value: string | null | undefined,
): ParsedSentryDsn | null {
  const dsn = (value ?? "").trim();
  if (!dsn) return null;
  let url: URL;
  try {
    url = new URL(dsn);
  } catch {
    return null;
  }
  const publicKey = decodeURIComponent(url.username || "");
  const projectId = url.pathname.replace(/^\//, "").split("/")[0];
  if (!publicKey || !projectId || !url.host) return null;
  return {
    dsn,
    publicKey,
    host: url.host,
    projectId,
    storeUrl: `${url.protocol}//${url.host}/api/${projectId}/store/`,
  };
}

export async function captureServerException(
  dsn: string | null | undefined,
  error: unknown,
  fetchImpl: typeof fetch = fetch,
): Promise<{ sent: boolean }> {
  const parsed = parseSentryDsn(dsn);
  if (!parsed) return { sent: false };
  const message = error instanceof Error ? error.message : String(error);
  const eventId = globalThis.crypto?.randomUUID?.()?.replace(/-/g, "") ??
    `${Date.now().toString(16)}${Math.random().toString(16).slice(2)}`.padEnd(
      32,
      "0",
    ).slice(0, 32);
  const event = {
    event_id: eventId,
    timestamp: new Date().toISOString(),
    platform: "javascript",
    level: "error",
    message: { formatted: message },
    exception: {
      values: [
        {
          type: error instanceof Error ? error.name : "Error",
          value: message,
        },
      ],
    },
  };
  try {
    const response = await fetchImpl(parsed.storeUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Sentry-Auth":
          `Sentry sentry_version=7, sentry_key=${parsed.publicKey}, sentry_client=fanjago/1.0`,
      },
      body: JSON.stringify(event),
    });
    return { sent: response.ok };
  } catch (sendError) {
    console.error("Error monitoring request failed", sendError);
    return { sent: false };
  }
}
